import { createEffect, createMemo, For, type JSX, on } from 'solid-js';
import { t } from '../i18n';
import { createRailFolds } from '../lib/rail-folds';
import type { NavGroup } from '../lib/routes';
import { AppNav, type AppNavItem } from './AppNav';

// The whole rail, ONE fold mechanism for every section (issue #5685): the
// container that owns the fold state (`lib/rail-folds.ts`, remembered in
// `dz-rail-folds`), the arriving-opens rule, and the single `<nav>` landmark.
// `AppNav` paints one section; this decides, for every section at once, which
// are open.
//
// Remembered state is GLOBAL, not org-scoped: the sections are jobs, not
// tenants, and a preference that follows you between orgs is what "I like the
// rail the way I left it" means. The fold store is created HERE, per mount —
// the rail lives as long as the shell does, so this is not a boot-lived cache
// (`standards/long-running-resources.md`).

/** One section of the rail: a `lib/rail.ts` group plus its live badges. */
export type RailSection = {
  readonly group: NavGroup;
  /** i18n key of the section heading; `RailGroups` translates it. */
  readonly heading: string;
  readonly items: readonly AppNavItem[];
};

export function RailGroups(props: {
  /** The `<nav>`'s accessible name (already translated). */
  readonly label: string;
  readonly groups: readonly RailSection[];
  readonly resolve: (href: string) => string;
  /**
   * The `href` of the ONE lit row on the whole rail (`currentRailItem`), or
   * undefined. Stated by the caller rather than matched per link: a per-link
   * prefix match lights Fleet AND Plans on `/fleet/plans`.
   */
  readonly current?: string | undefined;
  /** Which row keys are work somebody owes — what a folded heading sums. */
  readonly owed?: ReadonlySet<string> | undefined;
  /**
   * The rail is the collapsed icon rail (#7710): every section renders open (the
   * headings are hidden, so a folded section would be unreachable) and no fold is
   * written, so expanding restores the reader's folds as they were.
   */
  readonly collapsed?: boolean | undefined;
}): JSX.Element {
  const folds = createRailFolds();

  // Keyed on the lit ROW through a memo, so a deeper page under the same row
  // (`/repos` to `/repos/o/r`) is not a new arrival and does not reopen a
  // section the reader just closed.
  const lit = createMemo(() => props.current);

  let root: HTMLElement | undefined;
  // ARRIVING OPENS (issue #5685): land on a page and its section unfolds. A
  // page you LEAVE never closes its section — folding is only ever your own
  // click — so this effect reveals and never folds, and the lit row it reads
  // is `on(...)`'s dep, read NOWHERE else in the body: an effect that read the
  // fold while revealing would re-run on the very click that folded and
  // reopen the section in the same tick.
  createEffect(
    on(lit, (href) => {
      const home = props.groups.find((group) => group.items.some((item) => item.href === href));
      if (home === undefined) return;
      folds.reveal(home.group);
      // After the reveal commits, the region is on screen and the page you are
      // ON must be visible — a short viewport can still have it scrolled off
      // the rail. `block: 'nearest'` scrolls only if it has to, and only the
      // rail's own scroll port.
      queueMicrotask(() => {
        root?.querySelector('.app-nav__link--active')?.scrollIntoView({ block: 'nearest' });
      });
    }),
  );

  return (
    <nav
      class="app-nav"
      aria-label={props.label}
      ref={(el) => {
        root = el;
      }}
    >
      <For each={props.groups}>
        {(section) => (
          <AppNav
            heading={t(section.heading)}
            items={section.items}
            resolve={props.resolve}
            current={props.current}
            open={props.collapsed === true || folds.isOpen(section.group)}
            onToggle={() => {
              if (props.collapsed !== true) folds.toggle(section.group);
            }}
            onReveal={() => folds.reveal(section.group)}
            owed={props.owed}
          />
        )}
      </For>
    </nav>
  );
}
